import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { GpsInlineLoader } from "@/components/gps-loader";
import {
  Calendar as CalendarIcon,
  Zap,
  Info,
  Clock,
  Map as MapIcon,
  Gauge,
  Truck,
  BarChart3,
  ArrowLeftRight,
  Hexagon,
  Receipt,
  Terminal,
  ListOrdered,
  Tag,
  ShieldCheck,
  FileText,
  FileSpreadsheet,
  Eye,
  Search,
  ChevronDown,
  Check,
  X,
  Loader2,
  MapPin,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { NewGeofenceModal } from "@/routes/geofences";
import {
  useVehicles,
  useGeofences,
  useReportTrips,
  useReportRoutePositions,
  useReportRoute,
  useReportSummary,
  useReportEvents,
  useReportGeofenceEvents,
  useReportCommandEvents,
  useReportOnOffPeriods,
  useReportDrivingStopped,
} from "@/hooks/queries";
import { reportsService } from "@/services/reports.service";
import type { Vehicle, Geofence, Trip, RoutePoint, DetailedPosition } from "@/types";
import { downloadBlob, exportWorkbook, printReport, buildRouteMapHtml, formatDateTime, formatDuration } from "@/lib/reports-export";
import { ReportRouteMap, ROUTE_COLORS, type ReportRouteLine } from "@/components/report-route-map";
import { FleetDashboardReport } from "@/components/reports-dashboard";
import { TopAddressesReport } from "@/components/reports-top-addresses";
import { useAnyPermissionGateStatus } from "@/hooks/use-permission";
import { LicensePlate } from "@/components/license-plate";

// Catálogo "reports" não tem uma ação genérica "view" (ver
// gpsdrive-user-permissions.service.ts) — só as 4 sub-seções abaixo, cada
// uma um relatório diferente. Mesmo critério do item "Administração" na
// sidebar (useAnyPermission): libera a página se tiver QUALQUER uma
// habilitada, nunca todas de uma vez.
const REPORT_SECTION_ACTIONS = ["routes", "stops", "trips", "events"];

// Pedido 2026-09-01: Relatórios continua no menu mas mostra "Sem
// permissão" ao abrir sem nenhuma das ações acima, mesmo padrão de
// GeofencesPageGate/UsersPageGate/SettingsPageGate.
function ReportsPageGate() {
  // Bug real 2026-09-01: "aparece sem permissões e depois de segundos
  // aparece" — ver usePermissionGateStatus.
  const status = useAnyPermissionGateStatus("reports", REPORT_SECTION_ACTIONS);
  if (status === "loading") {
    return (
      <GpsInlineLoader className="h-[70vh]" />
    );
  }
  if (status === "denied") {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold">Sem permissão</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Você não tem acesso para visualizar relatórios. Peça a um administrador para conceder
            a permissão em Usuários → Editar → Permissões.
          </p>
        </div>
      </div>
    );
  }
  return <ReportsPage />;
}

export const Route = createFileRoute("/reports")({
  component: ReportsPageGate,
  head: () => ({ meta: [{ title: "Relatórios — Wp Rastreamento" }] }),
});

type ReportCard = {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  // false = recurso ainda não tem suporte na API do Traccar (ou não estava
  // no escopo desta etapa) — card fica visível, porém desabilitado, com
  // badge "🚧 Aguardando Backend GPS Drive". Nunca removido da interface.
  working: boolean;
};

const monitoramento: ReportCard[] = [
  { id: "painel", title: "Painel da Frota", description: "Resumo com gráficos: distância, velocidade, movimento e viagens", icon: BarChart3, color: "text-violet-500", working: true },
  { id: "on-off", title: "Ligado e desligado", description: "Ignição on/off com datas e durações", icon: Zap, color: "text-amber-500", working: true },
  { id: "info", title: "Informações Gerais", description: "Dados gerais do veículo", icon: Info, color: "text-sky-500", working: true },
  { id: "historico", title: "Histórico", description: "Histórico de posições e eventos", icon: Clock, color: "text-orange-500", working: true },
  { id: "rotas", title: "Rotas", description: "Rotas percorridas no período", icon: MapIcon, color: "text-blue-500", working: true },
  { id: "velocidade", title: "Velocidade", description: "Análise de velocidade por trecho", icon: Gauge, color: "text-slate-500", working: true },
  { id: "dir-parado", title: "Dirigindo e parado", description: "Tempos de condução e parada", icon: Truck, color: "text-rose-500", working: true },
];

const analises: ReportCard[] = [
  { id: "dir-simples", title: "Dirigindo e parado simplificado", description: "Versão simplificada dirigindo/parado", icon: BarChart3, color: "text-rose-500", working: false },
  { id: "comparar", title: "Comparar veículos", description: "Comparativo entre veículos", icon: ArrowLeftRight, color: "text-indigo-500", working: false },
  { id: "enderecos", title: "Endereços mais visitados", description: "Onde o veículo mais ficou parado: dias, horários e link do local", icon: MapPin, color: "text-emerald-500", working: true },
  { id: "cercas", title: "Cercas e Perímetros", description: "Entradas e saídas de geofences", icon: Hexagon, color: "text-slate-500", working: true },
  { id: "despesas", title: "Despesas", description: "Relatório de despesas por veículo", icon: Receipt, color: "text-slate-500", working: false },
];

const avancado: ReportCard[] = [
  { id: "comandos", title: "Comandos", description: "Histórico de comandos enviados", icon: Terminal, color: "text-sky-500", working: true },
  { id: "eventos", title: "Eventos", description: "Log de eventos do dispositivo", icon: ListOrdered, color: "text-blue-500", working: true },
  { id: "tag", title: "Tag", description: "Relatório por tags configuradas", icon: Tag, color: "text-amber-500", working: false },
  { id: "auditoria", title: "Auditoria de Logs", description: "Registro de ações na sua conta", icon: ShieldCheck, color: "text-emerald-500", working: false },
];

const PENDING_BADGE = "🚧 Aguardando Backend GPS Drive";

type TabId = "novo" | "agendados";
type FormatId = "view" | "pdf" | "excel";
type PeriodPreset = "hoje" | "ontem" | "6h" | "12h" | "24h" | "3dias" | "7dias" | "30dias" | "personalizado";
type HorarioMode = "dia" | "personalizado";

// Traduz o preset de período (+ recorte de horário, quando aplicável) num
// intervalo [from, to] real para consultar a API do Traccar. Presets
// relativos (6h/12h/24h/3dias/7dias) já são uma janela terminando agora —
// só presets "por dia" (hoje/ontem/personalizado) fazem sentido recortar
// por horário.
function periodRange(
  preset: PeriodPreset,
  custom: { from: string; to: string },
  horario: HorarioMode,
  timeFrom: string,
  timeTo: string,
): { from: Date; to: Date } {
  const now = new Date();
  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };

  let from: Date;
  let to: Date;
  const isDayBased = preset === "hoje" || preset === "ontem" || preset === "personalizado";

  switch (preset) {
    case "hoje":
      from = startOfDay(now);
      to = endOfDay(now);
      break;
    case "ontem": {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      from = startOfDay(y);
      to = endOfDay(y);
      break;
    }
    case "6h":
      from = new Date(now.getTime() - 6 * 3_600_000);
      to = now;
      break;
    case "12h":
      from = new Date(now.getTime() - 12 * 3_600_000);
      to = now;
      break;
    case "24h":
      from = new Date(now.getTime() - 24 * 3_600_000);
      to = now;
      break;
    case "3dias":
      from = new Date(now.getTime() - 3 * 86_400_000);
      to = now;
      break;
    case "7dias":
      from = new Date(now.getTime() - 7 * 86_400_000);
      to = now;
      break;
    case "30dias":
      from = new Date(now.getTime() - 30 * 86_400_000);
      to = now;
      break;
    case "personalizado":
      from = startOfDay(new Date(`${custom.from}T00:00:00`));
      to = endOfDay(new Date(`${custom.to}T00:00:00`));
      break;
  }

  if (horario === "personalizado" && isDayBased) {
    const [fh, fm] = timeFrom.split(":").map(Number);
    const [th, tm] = timeTo.split(":").map(Number);
    from = new Date(from);
    from.setHours(fh || 0, fm || 0, 0, 0);
    to = new Date(to);
    to.setHours(th ?? 23, tm ?? 59, 59, 999);
  }
  return { from, to };
}

const WORKING_REPORT_IDS = new Set(
  [...monitoramento, ...analises, ...avancado].filter((c) => c.working).map((c) => c.id),
);

interface GeneratedRequest {
  reportId: string;
  vehicleIds: string[];
  geofenceIds: string[];
  from: string; // ISO
  to: string; // ISO
  format: FormatId;
  key: number;
}

function ReportsPage() {
  const [tab, setTab] = useState<TabId>("novo");
  const [selected, setSelected] = useState<ReportCard>(monitoramento[0]);
  const [format, setFormat] = useState<FormatId>("view");
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  const [selectedGeofences, setSelectedGeofences] = useState<string[]>([]);
  const [periodo, setPeriodo] = useState<PeriodPreset>("hoje");
  const [horario, setHorario] = useState<HorarioMode>("dia");
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [timeFrom, setTimeFrom] = useState("00:00");
  const [timeTo, setTimeTo] = useState("23:59");
  const [createGeofenceOpen, setCreateGeofenceOpen] = useState(false);
  const [generated, setGenerated] = useState<GeneratedRequest | null>(null);
  const genCounter = useRef(0);

  const { data: vehicles = [] } = useVehicles();
  const { data: geofences = [] } = useGeofences();

  const filteredVehicles = useMemo(() => {
    const q = vehicleSearch.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter(
      (v) =>
        v.plate.toLowerCase().includes(q) ||
        v.name.toLowerCase().includes(q) ||
        (v.imei ?? "").toLowerCase().includes(q),
    );
  }, [vehicles, vehicleSearch]);

  const toggleVehicle = (id: string) =>
    setSelectedVehicles((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleGeofence = (id: string) =>
    setSelectedGeofences((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const isWorking = WORKING_REPORT_IDS.has(selected.id);

  const handleGenerate = () => {
    if (!isWorking) {
      toast.info(`${PENDING_BADGE} — este relatório ainda não está disponível.`);
      return;
    }
    if (selectedVehicles.length === 0) {
      toast.error("Selecione ao menos um veículo para gerar o relatório.");
      return;
    }
    const { from, to } = periodRange(periodo, { from: dateFrom, to: dateTo }, horario, timeFrom, timeTo);
    genCounter.current += 1;
    setGenerated({
      reportId: selected.id,
      vehicleIds: [...selectedVehicles],
      geofenceIds: [...selectedGeofences],
      from: from.toISOString(),
      to: to.toISOString(),
      format,
      key: genCounter.current,
    });
  };

  return (
    <div className="min-h-full bg-muted/30">
      <div className="px-6 pt-6 pb-2 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-primary/10 p-2">
            <BarChart3 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Relatórios</h1>
            <p className="text-xs text-muted-foreground">Gere relatórios da sua frota</p>
          </div>
        </div>
      </div>

      <div className="px-6 border-b bg-background">
        <div className="flex gap-6">
          {([
            { id: "novo", label: "Novo relatório" },
            { id: "agendados", label: "Relatórios agendados" },
          ] as { id: TabId; label: string }[]).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                "relative py-3 text-sm font-medium transition-colors",
                tab === t.id ? "text-primary" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
              {tab === t.id && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary" />}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 p-6">
        <div className="flex-1 space-y-8">
          {tab === "novo" && (
            <>
              <Section title="Monitoramento" cards={monitoramento} selectedId={selected.id} onSelect={setSelected} />
              <Section title="Análises" cards={analises} selectedId={selected.id} onSelect={setSelected} />
              <Section title="Avançado" cards={avancado} selectedId={selected.id} onSelect={setSelected} />
              {generated && <ReportResults request={generated} vehicles={vehicles} geofences={geofences} />}
            </>
          )}
          {tab === "agendados" && (
            <div className="rounded-lg border bg-background p-10 text-center text-sm text-muted-foreground space-y-3">
              <div>Nenhum relatório agendado.</div>
              <span className="inline-block text-[10px] font-medium rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                {PENDING_BADGE}
              </span>
            </div>
          )}
        </div>

        <aside className="w-full lg:w-[340px] shrink-0">
          <div className="rounded-lg border bg-background p-5 space-y-5 sticky top-6">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">Dados do relatório</h2>
              <button className="text-[11px] font-medium text-muted-foreground tracking-wider hover:text-foreground">
                LIMPAR
              </button>
            </div>

            <div>
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium",
                  isWorking ? "bg-orange-500/10 text-orange-600" : "bg-muted text-muted-foreground",
                )}
              >
                <selected.icon className="h-3.5 w-3.5" />
                {selected.title}
              </span>
              {!isWorking && (
                <span className="ml-2 inline-block text-[10px] font-medium rounded bg-muted px-1.5 py-0.5 text-muted-foreground align-middle">
                  {PENDING_BADGE}
                </span>
              )}
            </div>

            <Field
              label="Veículos"
              action={
                <GeofencesPopover
                  geofences={geofences}
                  selected={selectedGeofences}
                  onToggle={toggleGeofence}
                  onCreate={() => setCreateGeofenceOpen(true)}
                />
              }
            >
              <VehiclePicker
                vehicles={vehicles}
                filtered={filteredVehicles}
                search={vehicleSearch}
                onSearchChange={setVehicleSearch}
                selected={selectedVehicles}
                onToggle={toggleVehicle}
                onClear={() => setSelectedVehicles([])}
                onSelectAll={() => setSelectedVehicles(filteredVehicles.map((v) => v.id))}
              />
            </Field>

            <Field label="Período que será analisado">
              <Select value={periodo} onValueChange={(v) => setPeriodo(v as PeriodPreset)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hoje">Hoje</SelectItem>
                  <SelectItem value="ontem">Ontem</SelectItem>
                  <SelectItem value="6h">Últimas 6 horas</SelectItem>
                  <SelectItem value="12h">Últimas 12 horas</SelectItem>
                  <SelectItem value="24h">Últimas 24 horas</SelectItem>
                  <SelectItem value="3dias">Últimos 3 dias</SelectItem>
                  <SelectItem value="7dias">Últimos 7 dias</SelectItem>
                  <SelectItem value="30dias">Últimos 30 dias</SelectItem>
                  <SelectItem value="personalizado">Personalizado</SelectItem>
                </SelectContent>
              </Select>
              {periodo === "personalizado" && (
                <div className="mt-2 flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="flex-1 bg-transparent text-sm outline-none"
                  />
                  <span className="text-muted-foreground text-sm">—</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="flex-1 bg-transparent text-sm outline-none"
                  />
                </div>
              )}
            </Field>

            <Field label="Horário">
              <Select value={horario} onValueChange={(v) => setHorario(v as HorarioMode)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dia">Dia inteiro (00:00 - 23:59)</SelectItem>
                  <SelectItem value="personalizado">Intervalo personalizado</SelectItem>
                </SelectContent>
              </Select>
              {horario === "personalizado" && (
                <>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] text-muted-foreground">Início</label>
                      <input
                        type="time"
                        value={timeFrom}
                        onChange={(e) => setTimeFrom(e.target.value)}
                        className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-muted-foreground">Fim</label>
                      <input
                        type="time"
                        value={timeTo}
                        onChange={(e) => setTimeTo(e.target.value)}
                        className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>
                  {!(periodo === "hoje" || periodo === "ontem" || periodo === "personalizado") && (
                    <p className="mt-1.5 text-[11px] text-muted-foreground">
                      Só se aplica com período Hoje, Ontem ou Personalizado — períodos relativos (últimas Xh/dias) já são uma janela exata até agora.
                    </p>
                  )}
                </>
              )}
            </Field>

            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                O relatório será gerado em
              </label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <FormatButton active={format === "view"} onClick={() => setFormat("view")} icon={Eye} label="Visualização" />
                <FormatButton active={format === "pdf"} onClick={() => setFormat("pdf")} icon={FileText} label="PDF" />
                <FormatButton active={format === "excel"} onClick={() => setFormat("excel")} icon={FileSpreadsheet} label="Excel" />
              </div>
            </div>

            <Button className="w-full h-11 text-sm font-semibold tracking-wide" onClick={handleGenerate}>
              GERAR O RELATÓRIO
            </Button>
          </div>
        </aside>
      </div>
      {createGeofenceOpen && (
        <NewGeofenceModal
          onClose={() => setCreateGeofenceOpen(false)}
          onSaved={() => setCreateGeofenceOpen(false)}
        />
      )}
    </div>
  );
}

function Section({
  title,
  cards,
  selectedId,
  onSelect,
}: {
  title: string;
  cards: ReportCard[];
  selectedId: string;
  onSelect: (c: ReportCard) => void;
}) {
  return (
    <div>
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-primary/80 mb-3">{title}</h3>
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
        {cards.map((c) => {
          const active = c.id === selectedId;
          const Icon = c.icon;
          return (
            <button
              key={c.id}
              onClick={() => c.working && onSelect(c)}
              aria-disabled={!c.working}
              className={cn(
                "text-left rounded-lg border bg-background p-3 transition-all",
                c.working ? "hover:shadow-sm hover:border-primary/40" : "opacity-60 cursor-not-allowed",
                active && "border-primary ring-1 ring-primary/30 bg-primary/[0.03]",
              )}
            >
              <Icon className={cn("h-5 w-5 mb-2", c.color)} />
              <div className="text-sm font-medium leading-tight">{c.title}</div>
              <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{c.description}</div>
              {!c.working && (
                <div className="mt-2 inline-block text-[9px] font-medium leading-tight rounded bg-muted px-1 py-0.5 text-muted-foreground">
                  {PENDING_BADGE}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Field({
  label,
  action,
  children,
}: {
  label: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </label>
        {action}
      </div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function VehiclePicker({
  vehicles,
  filtered,
  search,
  onSearchChange,
  selected,
  onToggle,
  onClear,
  onSelectAll,
}: {
  vehicles: Vehicle[];
  filtered: Vehicle[];
  search: string;
  onSearchChange: (v: string) => void;
  selected: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
  onSelectAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedVehicles = vehicles.filter((v) => selected.includes(v.id));
  const allSelected = filtered.length > 0 && filtered.every((v) => selected.includes(v.id));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-10 rounded-md border border-input bg-background px-3 py-2 text-left text-sm hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-ring flex items-center gap-2"
        >
          <div className="flex-1 flex flex-wrap gap-1">
            {selectedVehicles.length === 0 && (
              <span className="text-muted-foreground">Selecione os veículos</span>
            )}
            {selectedVehicles.slice(0, 3).map((v) => (
              <span
                key={v.id}
                className="inline-flex items-center gap-1 rounded bg-primary/10 text-primary text-[11px] px-1.5 py-0.5"
              >
                {v.plate}
                <X
                  className="h-3 w-3 hover:opacity-70"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(v.id);
                  }}
                />
              </span>
            ))}
            {selectedVehicles.length > 3 && (
              <span className="text-[11px] text-muted-foreground">
                +{selectedVehicles.length - 3}
              </span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-0">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Busque por nome, placa ou IMEI"
              className="pl-7 h-9"
            />
          </div>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-b text-xs">
          <button className="text-primary hover:underline" onClick={onSelectAll}>
            {allSelected ? "Todos selecionados" : "Selecionar todos"}
          </button>
          <button className="text-muted-foreground hover:text-foreground" onClick={onClear}>
            Limpar
          </button>
        </div>
        <div className="max-h-64 overflow-auto py-1">
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              Nenhum veículo encontrado
            </div>
          )}
          {filtered.map((v) => {
            const active = selected.includes(v.id);
            return (
              <button
                key={v.id}
                onClick={() => onToggle(v.id)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/60"
              >
                <div
                  className={cn(
                    "h-4 w-4 rounded border flex items-center justify-center",
                    active ? "bg-primary border-primary" : "border-input",
                  )}
                >
                  {active && <Check className="h-3 w-3 text-primary-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{v.name}</div>
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><LicensePlate plate={v.plate} size="xs" />{v.imei ? v.imei : ""}</div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function GeofencesPopover({
  geofences,
  selected,
  onToggle,
  onCreate,
}: {
  geofences: Geofence[];
  selected: string[];
  onToggle: (id: string) => void;
  onCreate: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return geofences;
    return geofences.filter((g) => g.name.toLowerCase().includes(q));
  }, [geofences, search]);
  const allSelected = filtered.length > 0 && filtered.every((g) => selected.includes(String(g.id)));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="text-xs text-primary hover:underline underline-offset-2">
          Perímetros{selected.length > 0 && ` (${selected.length})`}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] p-0">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Busque uma cerca"
              className="pl-7 h-9"
            />
          </div>
        </div>
        <div className="px-3 py-2 border-b text-center">
          <button
            className="text-xs text-primary hover:underline"
            onClick={() => {
              setOpen(false);
              onCreate();
            }}
          >
            Criar perímetro
          </button>
        </div>
        <div className="px-3 py-2 border-b text-center">
          <button
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            onClick={() => {
              if (allSelected) {
                filtered.forEach((g) => selected.includes(String(g.id)) && onToggle(String(g.id)));
              } else {
                filtered.forEach((g) => !selected.includes(String(g.id)) && onToggle(String(g.id)));
              }
            }}
          >
            Selecionar todos <ChevronDown className="h-3 w-3" />
          </button>
        </div>
        <div className="max-h-56 overflow-auto py-1">
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              Nenhum perímetro encontrado
            </div>
          )}
          {filtered.map((g) => {
            const active = selected.includes(String(g.id));
            return (
              <button
                key={g.id}
                onClick={() => onToggle(String(g.id))}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/60"
              >
                <div
                  className={cn(
                    "h-4 w-4 rounded border flex items-center justify-center",
                    active ? "bg-primary border-primary" : "border-input",
                  )}
                >
                  {active && <Check className="h-3 w-3 text-primary-foreground" />}
                </div>
                <span className="text-sm truncate">{g.name}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FormatButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-col items-center gap-1 rounded-md border p-2 text-[11px] font-medium transition-colors",
        active ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:text-foreground",
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

// ============================================================================
// Resultados — cada relatório "working" busca dado real do Traccar via
// hooks/queries e renderiza visualização + dispara exportação (PDF via
// impressão isolada, Excel via lib xlsx ou passthrough nativo do Traccar).
// Só monta depois de "GERAR O RELATÓRIO" (estado `generated` no componente
// pai) — nunca reaproveita resultado de uma geração anterior com outro
// filtro, porque a query key inclui veículos+período (ver queryKeys.reportKey).
// ============================================================================

// Dispara a exportação (PDF/Excel) uma única vez por geração, assim que o
// dado estiver pronto. `request.key` muda a cada clique em "GERAR O
// RELATÓRIO", então uma nova geração sempre pode exportar de novo mesmo com
// o mesmo tipo de relatório/veículos/período do clique anterior.
function useExportOnce(request: GeneratedRequest, ready: boolean, run: () => void) {
  const doneRef = useRef<number | null>(null);
  useEffect(() => {
    if (request.format === "view" || !ready) return;
    if (doneRef.current === request.key) return;
    doneRef.current = request.key;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.format, request.key, ready]);
}

function ResultShell({
  title,
  loading,
  count,
  children,
}: {
  title: string;
  loading: boolean;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-background p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">{title}</h3>
        {count != null && <span className="text-xs text-muted-foreground">{count} registro(s)</span>}
      </div>
      {loading ? (
        <div className="py-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando dados reais…
        </div>
      ) : (
        children
      )}
    </div>
  );
}

type ResultProps = { request: GeneratedRequest; vehicleName: (id: string) => string };

function ReportResults({
  request,
  vehicles,
  geofences,
}: {
  request: GeneratedRequest;
  vehicles: Vehicle[];
  geofences: Geofence[];
}) {
  const vehicleName = (id: string) => vehicles.find((v) => v.id === id)?.name ?? id;

  switch (request.reportId) {
    case "painel":
      return <FleetDashboardReport vehicleIds={request.vehicleIds} from={request.from} to={request.to} vehicleName={vehicleName} autoPdfKey={request.format === "pdf" ? request.key : undefined} />;
    case "historico":
      return <HistoricoResult request={request} vehicleName={vehicleName} />;
    case "rotas":
      return <RotasResult request={request} vehicleName={vehicleName} />;
    case "velocidade":
      return <VelocidadeResult request={request} vehicleName={vehicleName} />;
    case "on-off":
      return <OnOffResult request={request} vehicleName={vehicleName} />;
    case "dir-parado":
      return <DirParadoResult request={request} />;
    case "info":
      return <InfoResult request={request} vehicles={vehicles.filter((v) => request.vehicleIds.includes(v.id))} />;
    case "eventos":
      return <EventosResult request={request} vehicleName={vehicleName} />;
    case "enderecos":
      return <TopAddressesReport vehicleIds={request.vehicleIds} from={request.from} to={request.to} vehicleName={vehicleName} format={request.format} requestKey={request.key} />;
    case "cercas":
      return <CercasResult request={request} vehicleName={vehicleName} geofences={geofences} />;
    case "comandos":
      return <ComandosResult request={request} vehicleName={vehicleName} />;
    default:
      return null;
  }
}

function HistoricoResult({ request, vehicleName }: ResultProps) {
  const { data = [], isLoading } = useReportRoutePositions(request.vehicleIds, request.from, request.to, true);

  useExportOnce(request, !isLoading, () => {
    if (request.format === "excel") {
      reportsService
        .exportExcel("route", request.vehicleIds, request.from, request.to)
        .then((blob) => downloadBlob(blob, `historico-${Date.now()}.xlsx`))
        .catch(() => toast.error("Falha ao exportar Excel do histórico."));
    } else if (request.format === "pdf") {
      const rows = data
        .map(
          (p) =>
            `<tr><td>${vehicleName(p.vehicleId)}</td><td>${formatDateTime(p.timestamp)}</td><td>${p.speed} km/h</td><td>${
              p.ignition == null ? "—" : p.ignition ? "Ligada" : "Desligada"
            }</td><td>${p.address ?? "—"}</td></tr>`,
        )
        .join("");
      printReport(
        "Relatório de Histórico",
        `<h1>Histórico</h1><div class="subtitle">${formatDateTime(request.from)} — ${formatDateTime(
          request.to,
        )}</div><table><thead><tr><th>Veículo</th><th>Data/Hora</th><th>Velocidade</th><th>Ignição</th><th>Endereço</th></tr></thead><tbody>${rows}</tbody></table>`,
      );
    }
  });

  return (
    <ResultShell title="Histórico" loading={isLoading} count={data.length}>
      <div className="max-h-[420px] overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Veículo</TableHead>
              <TableHead>Data/Hora</TableHead>
              <TableHead>Velocidade</TableHead>
              <TableHead>Ignição</TableHead>
              <TableHead>Endereço</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.slice(0, 500).map((p) => (
              <TableRow key={p.id}>
                <TableCell>{vehicleName(p.vehicleId)}</TableCell>
                <TableCell className="font-mono text-xs">{formatDateTime(p.timestamp)}</TableCell>
                <TableCell>{p.speed} km/h</TableCell>
                <TableCell>
                  {p.ignition == null ? "—" : p.ignition ? <Badge>Ligada</Badge> : <Badge variant="secondary">Desligada</Badge>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate">{p.address ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.length === 0 && !isLoading && (
          <div className="p-8 text-center text-sm text-muted-foreground">Nenhuma posição encontrada no período.</div>
        )}
      </div>
      {data.length > 500 && (
        <p className="mt-2 text-xs text-muted-foreground">
          Mostrando as primeiras 500 de {data.length} posições. Exporte em Excel/PDF para ver tudo.
        </p>
      )}
    </ResultShell>
  );
}

// GPS às vezes manda fix (0,0) ou fora da faixa válida — jogaria o mapa no
// meio do oceano e estragaria o enquadramento.
function validRoutePoints(points: RoutePoint[]) {
  return points.filter(
    (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(p.lat === 0 && p.lng === 0) && Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180,
  );
}

// Uma linha por veículo (cor própria), na ordem dos veículos do relatório.
function routeLinesByVehicle(points: RoutePoint[], vehicleIds: string[]): ReportRouteLine[] {
  const byVehicle = new Map<string, RoutePoint[]>();
  for (const p of validRoutePoints(points)) {
    const id = p.vehicleId ?? vehicleIds[0];
    const list = byVehicle.get(id);
    if (list) list.push(p);
    else byVehicle.set(id, [p]);
  }
  return vehicleIds
    .filter((id) => byVehicle.has(id))
    .map((id, i) => ({ key: id, color: ROUTE_COLORS[i % ROUTE_COLORS.length], points: byVehicle.get(id)! }));
}

function TripMapPreview({ trip }: { trip: Trip }) {
  const { data = [], isLoading } = useReportRoute([trip.vehicleId], trip.startTime, trip.endTime, true);
  const lines = useMemo(() => routeLinesByVehicle(data, [trip.vehicleId]), [data, trip.vehicleId]);
  if (isLoading) return <div className="p-4 text-xs text-muted-foreground">Carregando trajeto…</div>;
  return (
    <div className="border-t p-3">
      <ReportRouteMap lines={lines} height={300} />
      <div className="flex justify-between mt-1 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-600" /> Início · {trip.startAddress ?? "—"}</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-600" /> Fim · {trip.endAddress ?? "—"}</span>
      </div>
    </div>
  );
}

function RotasResult({ request, vehicleName }: ResultProps) {
  const { data = [], isLoading } = useReportTrips(request.vehicleIds, request.from, request.to, true);
  // Trajeto do período inteiro — mapa geral no topo da tela e no PDF.
  const { data: routePoints = [], isLoading: loadingRoute } = useReportRoute(request.vehicleIds, request.from, request.to, true);
  const lines = useMemo(() => routeLinesByVehicle(routePoints, request.vehicleIds), [routePoints, request.vehicleIds]);
  const [openTripId, setOpenTripId] = useState<string | null>(null);

  useExportOnce(request, !isLoading && (request.format !== "pdf" || !loadingRoute), () => {
    if (request.format === "excel") {
      reportsService
        .exportExcel("trips", request.vehicleIds, request.from, request.to)
        .then((blob) => downloadBlob(blob, `rotas-${Date.now()}.xlsx`))
        .catch(() => toast.error("Falha ao exportar Excel de rotas."));
    } else if (request.format === "pdf") {
      if (data.length === 0 && lines.length === 0) {
        toast.info("Nenhuma rota encontrada no período — nada para gerar no PDF.");
        return;
      }
      const legend =
        lines.length > 1
          ? `<div class="meta">${lines
              .map((l) => `<span><b style="color:${l.color}">●</b> ${vehicleName(l.key)}</span>`)
              .join("")}</div>`
          : "";
      const map = buildRouteMapHtml(lines.map((l) => ({ color: l.color, points: l.points })), 700, 380);
      const totalKm = data.reduce((s, t) => s + t.distanceKm, 0);
      const rows = data
        .map(
          (t) =>
            `<tr><td>${t.vehicleName || vehicleName(t.vehicleId)}</td><td>${formatDateTime(t.startTime)}</td><td>${formatDateTime(
              t.endTime,
            )}</td><td>${formatDuration(t.durationMs)}</td><td>${t.distanceKm.toFixed(1)} km</td><td>${t.averageSpeed} km/h</td><td>${
              t.maxSpeed
            } km/h</td><td>${t.startAddress ?? "—"}</td><td>${t.endAddress ?? "—"}</td></tr>`,
        )
        .join("");
      printReport(
        "Relatório de Rotas",
        `<h1>Rotas</h1><div class="subtitle">${formatDateTime(request.from)} — ${formatDateTime(
          request.to,
        )}</div><div class="meta"><span>Viagens: <b>${data.length}</b></span><span>Distância total: <b>${totalKm.toFixed(
          1,
        )} km</b></span></div>${legend}${map}<table><thead><tr><th>Veículo</th><th>Início</th><th>Fim</th><th>Duração</th><th>Distância</th><th>Vel. média</th><th>Vel. máxima</th><th>Endereço início</th><th>Endereço fim</th></tr></thead><tbody>${
          rows || `<tr><td colspan="9">Nenhuma viagem detectada no período.</td></tr>`
        }</tbody></table>`,
      );
    }
  });

  return (
    <ResultShell title="Rotas" loading={isLoading} count={data.length}>
      <div className="space-y-2">
        <div className="mb-3">
          {loadingRoute ? (
            <div className="p-4 text-xs text-muted-foreground">Carregando trajeto do período…</div>
          ) : (
            <>
              <ReportRouteMap lines={lines} height={380} />
              {lines.length > 1 && (
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                  {lines.map((l) => (
                    <span key={l.key} className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full" style={{ background: l.color }} /> {vehicleName(l.key)}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        {data.length === 0 && !isLoading && (
          <div className="p-8 text-center text-sm text-muted-foreground">Nenhuma rota encontrada no período.</div>
        )}
        {data.map((t) => (
          <div key={t.id} className="rounded-md border">
            <button
              onClick={() => setOpenTripId(openTripId === t.id ? null : t.id)}
              className="w-full flex flex-wrap items-center gap-x-6 gap-y-1 p-3 text-left hover:bg-muted/40"
            >
              <span className="text-sm font-medium">{t.vehicleName || vehicleName(t.vehicleId)}</span>
              <span className="text-xs text-muted-foreground">{formatDateTime(t.startTime)} → {formatDateTime(t.endTime)}</span>
              <span className="text-xs">{t.distanceKm.toFixed(1)} km</span>
              <span className="text-xs">{formatDuration(t.durationMs)}</span>
              <span className="text-xs">méd {t.averageSpeed} · máx {t.maxSpeed} km/h</span>
              <ChevronDown
                className={cn("h-3.5 w-3.5 ml-auto text-muted-foreground transition-transform", openTripId === t.id && "rotate-180")}
              />
            </button>
            {openTripId === t.id && <TripMapPreview trip={t} />}
          </div>
        ))}
      </div>
    </ResultShell>
  );
}

function SpeedChart({ positions }: { positions: DetailedPosition[] }) {
  const width = 640;
  const height = 140;
  const padding = 24;
  const speeds = positions.map((p) => p.speed);
  const maxSpeed = Math.max(...speeds, 1);
  const avg = speeds.reduce((a, b) => a + b, 0) / speeds.length;
  const stepX = (width - padding * 2) / Math.max(1, positions.length - 1);
  const points = speeds
    .map((s, i) => {
      const x = padding + i * stepX;
      const y = height - padding - (s / maxSpeed) * (height - padding * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const avgY = height - padding - (avg / maxSpeed) * (height - padding * 2);
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground mb-2">Velocidade ao longo do período (linha tracejada = média)</div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
        <line x1={padding} y1={avgY} x2={width - padding} y2={avgY} stroke="currentColor" strokeOpacity={0.3} strokeDasharray="4 4" />
        <polyline points={points} fill="none" stroke="#3b82f6" strokeWidth={1.5} />
      </svg>
    </div>
  );
}

function VelocidadeResult({ request, vehicleName }: ResultProps) {
  const { data: summary = [], isLoading: loadingSummary } = useReportSummary(request.vehicleIds, request.from, request.to, true);
  const { data: positions = [], isLoading: loadingPositions } = useReportRoutePositions(
    request.vehicleIds,
    request.from,
    request.to,
    true,
  );
  const isLoading = loadingSummary || loadingPositions;

  useExportOnce(request, !isLoading, () => {
    if (request.format === "excel") {
      exportWorkbook(
        [
          {
            name: "Resumo",
            rows: [
              ["Veículo", "Distância (km)", "Vel. média (km/h)", "Vel. máxima (km/h)"],
              ...summary.map((s) => [s.vehicleName || vehicleName(s.vehicleId), s.distanceKm.toFixed(1), s.averageSpeed, s.maxSpeed]),
            ],
          },
          {
            name: "Velocidades",
            rows: [
              ["Veículo", "Data/Hora", "Velocidade (km/h)"],
              ...positions.map((p) => [vehicleName(p.vehicleId), formatDateTime(p.timestamp), p.speed]),
            ],
          },
        ],
        `velocidade-${Date.now()}.xlsx`,
      );
    } else if (request.format === "pdf") {
      const summaryRows = summary
        .map(
          (s) =>
            `<tr><td>${s.vehicleName || vehicleName(s.vehicleId)}</td><td>${s.distanceKm.toFixed(1)} km</td><td>${s.averageSpeed} km/h</td><td>${s.maxSpeed} km/h</td></tr>`,
        )
        .join("");
      printReport(
        "Relatório de Velocidade",
        `<h1>Velocidade</h1><div class="subtitle">${formatDateTime(request.from)} — ${formatDateTime(
          request.to,
        )}</div><table><thead><tr><th>Veículo</th><th>Distância</th><th>Vel. média</th><th>Vel. máxima</th></tr></thead><tbody>${summaryRows}</tbody></table>`,
      );
    }
  });

  return (
    <ResultShell title="Velocidade" loading={isLoading}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {summary.map((s) => (
          <div key={s.vehicleId} className="rounded-md border p-3">
            <div className="text-xs text-muted-foreground truncate">{s.vehicleName || vehicleName(s.vehicleId)}</div>
            <div className="text-lg font-semibold mt-1">
              {s.maxSpeed} <span className="text-xs font-normal text-muted-foreground">km/h máx</span>
            </div>
            <div className="text-xs text-muted-foreground">média {s.averageSpeed} km/h</div>
          </div>
        ))}
        {summary.length === 0 && !isLoading && (
          <div className="col-span-full text-sm text-muted-foreground">Nenhum dado de velocidade no período.</div>
        )}
      </div>
      {positions.length > 1 && <SpeedChart positions={positions} />}
    </ResultShell>
  );
}

function OnOffResult({ request, vehicleName }: ResultProps) {
  const { data = [], isLoading } = useReportOnOffPeriods(request.vehicleIds, request.from, request.to, true);

  useExportOnce(request, !isLoading, () => {
    const rows: (string | number)[][] = [
      ["Veículo", "Estado", "Início", "Fim", "Duração"],
      ...data.map((p) => [
        vehicleName(p.vehicleId),
        p.state === "on" ? "Ligada" : "Desligada",
        formatDateTime(p.startTime),
        p.endTime ? formatDateTime(p.endTime) : "—",
        p.durationMs != null ? formatDuration(p.durationMs) : "—",
      ]),
    ];
    if (request.format === "excel") {
      exportWorkbook([{ name: "Ligado-Desligado", rows }], `ligado-desligado-${Date.now()}.xlsx`);
    } else if (request.format === "pdf") {
      const body = data
        .map(
          (p) =>
            `<tr><td>${vehicleName(p.vehicleId)}</td><td>${p.state === "on" ? "Ligada" : "Desligada"}</td><td>${formatDateTime(
              p.startTime,
            )}</td><td>${p.endTime ? formatDateTime(p.endTime) : "—"}</td><td>${
              p.durationMs != null ? formatDuration(p.durationMs) : "—"
            }</td></tr>`,
        )
        .join("");
      printReport(
        "Relatório Ligado/Desligado",
        `<h1>Ligado/Desligado</h1><div class="subtitle">${formatDateTime(request.from)} — ${formatDateTime(
          request.to,
        )}</div><table><thead><tr><th>Veículo</th><th>Estado</th><th>Início</th><th>Fim</th><th>Duração</th></tr></thead><tbody>${body}</tbody></table>`,
      );
    }
  });

  return (
    <ResultShell title="Ligado e desligado" loading={isLoading} count={data.length}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Veículo</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Início</TableHead>
            <TableHead>Fim</TableHead>
            <TableHead>Duração</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((p, i) => (
            <TableRow key={i}>
              <TableCell>{vehicleName(p.vehicleId)}</TableCell>
              <TableCell>{p.state === "on" ? <Badge>Ligada</Badge> : <Badge variant="secondary">Desligada</Badge>}</TableCell>
              <TableCell className="font-mono text-xs">{formatDateTime(p.startTime)}</TableCell>
              <TableCell className="font-mono text-xs">{p.endTime ? formatDateTime(p.endTime) : "—"}</TableCell>
              <TableCell>{p.durationMs != null ? formatDuration(p.durationMs) : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data.length === 0 && !isLoading && (
        <div className="p-8 text-center text-sm text-muted-foreground">Nenhum evento de ignição no período.</div>
      )}
    </ResultShell>
  );
}

function DirParadoResult({ request }: { request: GeneratedRequest }) {
  const { data = [], isLoading } = useReportDrivingStopped(request.vehicleIds, request.from, request.to, true);

  useExportOnce(request, !isLoading, () => {
    const rows: (string | number)[][] = [
      ["Veículo", "Dirigindo", "Parado", "Ocioso", "Quilometragem"],
      ...data.map((s) => [
        s.vehicleName,
        formatDuration(s.drivingMs),
        formatDuration(s.stoppedMs),
        s.idleMs != null ? formatDuration(s.idleMs) : "não disponível",
        `${s.distanceKm.toFixed(1)} km`,
      ]),
    ];
    if (request.format === "excel") {
      exportWorkbook([{ name: "Dirigindo-Parado", rows }], `dirigindo-parado-${Date.now()}.xlsx`);
    } else if (request.format === "pdf") {
      const body = data
        .map(
          (s) =>
            `<tr><td>${s.vehicleName}</td><td>${formatDuration(s.drivingMs)}</td><td>${formatDuration(s.stoppedMs)}</td><td>${
              s.idleMs != null ? formatDuration(s.idleMs) : "não disponível"
            }</td><td>${s.distanceKm.toFixed(1)} km</td></tr>`,
        )
        .join("");
      printReport(
        "Relatório Dirigindo/Parado",
        `<h1>Dirigindo/Parado</h1><div class="subtitle">${formatDateTime(request.from)} — ${formatDateTime(
          request.to,
        )}</div><table><thead><tr><th>Veículo</th><th>Dirigindo</th><th>Parado</th><th>Ocioso</th><th>Quilometragem</th></tr></thead><tbody>${body}</tbody></table>`,
      );
    }
  });

  return (
    <ResultShell title="Dirigindo e parado" loading={isLoading} count={data.length}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {data.map((s) => (
          <div key={s.vehicleId} className="rounded-md border p-3 space-y-1.5">
            <div className="text-xs font-medium truncate">{s.vehicleName}</div>
            <div className="text-xs text-muted-foreground">Dirigindo: <span className="text-foreground">{formatDuration(s.drivingMs)}</span></div>
            <div className="text-xs text-muted-foreground">Parado: <span className="text-foreground">{formatDuration(s.stoppedMs)}</span></div>
            <div className="text-xs text-muted-foreground">
              Ocioso: <span className="text-foreground">{s.idleMs != null ? formatDuration(s.idleMs) : "não disponível"}</span>
            </div>
            <div className="text-xs text-muted-foreground">Quilometragem: <span className="text-foreground">{s.distanceKm.toFixed(1)} km</span></div>
          </div>
        ))}
      </div>
      {data.length === 0 && !isLoading && <div className="p-8 text-center text-sm text-muted-foreground">Sem dados no período.</div>}
    </ResultShell>
  );
}

function InfoResult({ vehicles, request }: { vehicles: Vehicle[]; request: GeneratedRequest }) {
  useExportOnce(request, true, () => {
    const rows: (string | number)[][] = [
      ["Veículo", "Placa", "IMEI", "Status", "Velocidade atual", "Ignição", "Odômetro", "Bateria rastreador", "Última conexão"],
      ...vehicles.map((v) => [
        v.name,
        v.plate,
        v.imei ?? "—",
        v.status,
        `${v.speed} km/h`,
        v.ignition == null ? "—" : v.ignition ? "Ligada" : "Desligada",
        v.odometer != null ? `${v.odometer} km` : "—",
        v.batteryLevel != null ? `${v.batteryLevel}%` : "—",
        formatDateTime(v.lastConnection),
      ]),
    ];
    if (request.format === "excel") {
      exportWorkbook([{ name: "Informações Gerais", rows }], `informacoes-gerais-${Date.now()}.xlsx`);
    } else if (request.format === "pdf") {
      const body = vehicles
        .map(
          (v) =>
            `<tr><td>${v.name}</td><td>${v.plate}</td><td>${v.imei ?? "—"}</td><td>${v.status}</td><td>${v.speed} km/h</td><td>${
              v.ignition == null ? "—" : v.ignition ? "Ligada" : "Desligada"
            }</td><td>${v.odometer != null ? `${v.odometer} km` : "—"}</td><td>${formatDateTime(v.lastConnection)}</td></tr>`,
        )
        .join("");
      printReport(
        "Informações Gerais",
        `<h1>Informações Gerais</h1><table><thead><tr><th>Veículo</th><th>Placa</th><th>IMEI</th><th>Status</th><th>Velocidade</th><th>Ignição</th><th>Odômetro</th><th>Última conexão</th></tr></thead><tbody>${body}</tbody></table>`,
      );
    }
  });

  return (
    <ResultShell title="Informações Gerais" loading={false} count={vehicles.length}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {vehicles.map((v) => (
          <div key={v.id} className="rounded-md border p-3 space-y-1">
            <div className="text-sm font-medium">{v.name}</div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><LicensePlate plate={v.plate} size="xs" /> IMEI {v.imei ?? "—"}</div>
            <div className="text-xs text-muted-foreground">Status: <span className="text-foreground">{v.status}</span></div>
            <div className="text-xs text-muted-foreground">Velocidade: <span className="text-foreground">{v.speed} km/h</span></div>
            <div className="text-xs text-muted-foreground">
              Ignição: <span className="text-foreground">{v.ignition == null ? "—" : v.ignition ? "Ligada" : "Desligada"}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Odômetro: <span className="text-foreground">{v.odometer != null ? `${v.odometer} km` : "—"}</span>
            </div>
            <div className="text-xs text-muted-foreground">Última conexão: <span className="text-foreground">{formatDateTime(v.lastConnection)}</span></div>
          </div>
        ))}
      </div>
      {vehicles.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Selecione ao menos um veículo.</div>}
    </ResultShell>
  );
}

function EventosResult({ request, vehicleName }: ResultProps) {
  const { data = [], isLoading } = useReportEvents(request.vehicleIds, request.from, request.to, true);

  useExportOnce(request, !isLoading, () => {
    if (request.format === "excel") {
      reportsService
        .exportExcel("events", request.vehicleIds, request.from, request.to)
        .then((blob) => downloadBlob(blob, `eventos-${Date.now()}.xlsx`))
        .catch(() => toast.error("Falha ao exportar Excel de eventos."));
    } else if (request.format === "pdf") {
      const body = data.map((e) => `<tr><td>${vehicleName(e.vehicleId)}</td><td>${e.type}</td><td>${formatDateTime(e.eventTime)}</td></tr>`).join("");
      printReport(
        "Relatório de Eventos",
        `<h1>Eventos</h1><div class="subtitle">${formatDateTime(request.from)} — ${formatDateTime(
          request.to,
        )}</div><table><thead><tr><th>Veículo</th><th>Tipo</th><th>Data/Hora</th></tr></thead><tbody>${body}</tbody></table>`,
      );
    }
  });

  return (
    <ResultShell title="Eventos" loading={isLoading} count={data.length}>
      <div className="max-h-[420px] overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Veículo</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Data/Hora</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((e) => (
              <TableRow key={e.id}>
                <TableCell>{vehicleName(e.vehicleId)}</TableCell>
                <TableCell>{e.type}</TableCell>
                <TableCell className="font-mono text-xs">{formatDateTime(e.eventTime)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data.length === 0 && !isLoading && <div className="p-8 text-center text-sm text-muted-foreground">Nenhum evento no período.</div>}
    </ResultShell>
  );
}

function CercasResult({
  request,
  vehicleName,
  geofences,
}: ResultProps & { geofences: Geofence[] }) {
  const { data = [], isLoading } = useReportGeofenceEvents(request.vehicleIds, request.from, request.to, true);
  const filtered =
    request.geofenceIds.length === 0
      ? data
      : data.filter((e) => e.geofenceId != null && request.geofenceIds.includes(String(e.geofenceId)));
  const geofenceName = (id?: number) => geofences.find((g) => g.id === id)?.name ?? (id != null ? `#${id}` : "—");

  useExportOnce(request, !isLoading, () => {
    const extra: [string, string][] = [
      ["type", "geofenceEnter"],
      ["type", "geofenceExit"],
    ];
    if (request.format === "excel") {
      reportsService
        .exportExcel("events", request.vehicleIds, request.from, request.to, extra)
        .then((blob) => downloadBlob(blob, `cercas-${Date.now()}.xlsx`))
        .catch(() => toast.error("Falha ao exportar Excel de cercas."));
    } else if (request.format === "pdf") {
      const body = filtered
        .map(
          (e) =>
            `<tr><td>${vehicleName(e.vehicleId)}</td><td>${geofenceName(e.geofenceId)}</td><td>${
              e.type === "geofenceEnter" ? "Entrada" : "Saída"
            }</td><td>${formatDateTime(e.eventTime)}</td></tr>`,
        )
        .join("");
      printReport(
        "Relatório de Cercas e Perímetros",
        `<h1>Cercas e Perímetros</h1><div class="subtitle">${formatDateTime(request.from)} — ${formatDateTime(
          request.to,
        )}</div><table><thead><tr><th>Veículo</th><th>Cerca</th><th>Tipo</th><th>Data/Hora</th></tr></thead><tbody>${body}</tbody></table>`,
      );
    }
  });

  return (
    <ResultShell title="Cercas e Perímetros" loading={isLoading} count={filtered.length}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Veículo</TableHead>
            <TableHead>Cerca</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Data/Hora</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((e) => (
            <TableRow key={e.id}>
              <TableCell>{vehicleName(e.vehicleId)}</TableCell>
              <TableCell>{geofenceName(e.geofenceId)}</TableCell>
              <TableCell>{e.type === "geofenceEnter" ? <Badge>Entrada</Badge> : <Badge variant="secondary">Saída</Badge>}</TableCell>
              <TableCell className="font-mono text-xs">{formatDateTime(e.eventTime)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {filtered.length === 0 && !isLoading && (
        <div className="p-8 text-center text-sm text-muted-foreground">Nenhuma entrada/saída de geocerca no período.</div>
      )}
    </ResultShell>
  );
}

function ComandosResult({ request, vehicleName }: ResultProps) {
  const { data = [], isLoading } = useReportCommandEvents(request.vehicleIds, request.from, request.to, true);

  useExportOnce(request, !isLoading, () => {
    const extra: [string, string][] = [
      ["type", "commandResult"],
      ["type", "queuedCommandSent"],
    ];
    if (request.format === "excel") {
      reportsService
        .exportExcel("events", request.vehicleIds, request.from, request.to, extra)
        .then((blob) => downloadBlob(blob, `comandos-${Date.now()}.xlsx`))
        .catch(() => toast.error("Falha ao exportar Excel de comandos."));
    } else if (request.format === "pdf") {
      const body = data
        .map(
          (c) =>
            `<tr><td>${vehicleName(c.vehicleId)}</td><td>${
              c.type === "commandResult" ? "Resposta do rastreador" : "Comando entregue"
            }</td><td>${formatDateTime(c.eventTime)}</td><td>${c.result ?? "—"}</td></tr>`,
        )
        .join("");
      printReport(
        "Relatório de Comandos",
        `<h1>Comandos</h1><div class="subtitle">${formatDateTime(request.from)} — ${formatDateTime(
          request.to,
        )}</div><table><thead><tr><th>Veículo</th><th>Tipo</th><th>Data/Hora</th><th>Resultado</th></tr></thead><tbody>${body}</tbody></table>`,
      );
    }
  });

  return (
    <ResultShell title="Comandos" loading={isLoading} count={data.length}>
      <p className="text-xs text-muted-foreground mb-3">
        O sistema não mantém um endpoint de histórico de comandos dedicado — esta lista usa os eventos reais de
        resposta/entrega (<span className="font-mono">commandResult</span> / <span className="font-mono">queuedCommandSent</span>).
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Veículo</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Data/Hora</TableHead>
            <TableHead>Resultado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((c) => (
            <TableRow key={c.id}>
              <TableCell>{vehicleName(c.vehicleId)}</TableCell>
              <TableCell>{c.type === "commandResult" ? "Resposta do rastreador" : "Comando entregue"}</TableCell>
              <TableCell className="font-mono text-xs">{formatDateTime(c.eventTime)}</TableCell>
              <TableCell className="text-xs">{c.result ?? "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data.length === 0 && !isLoading && (
        <div className="p-8 text-center text-sm text-muted-foreground">Nenhum comando registrado no período.</div>
      )}
    </ResultShell>
  );
}
